import * as React from 'react';
import { connect } from 'react-redux';

import Spinner from 'components/spinner/Spinner';
import { ModalFrame } from 'components/modal';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import GenericListsModal from 'components/generic-lists-modal/GenericListsModal';
import CollectionImportedModalContent from 'components/modal/collection-imported-modal-content/CollectionImportedModalContent';

import { fetchParticipants, importCollection } from 'modules/campaigns/reducers/campaignParticipants';
import { fetchCampaignOverview } from 'modules/campaigns/reducers/campaignOverview';
import { fetchCampaignDashboardStatistics } from 'modules/campaigns/reducers/dashboard/campaignDashboardStatistics';

import { danger } from 'redux-store/reducers/common-reducer';

import { __t, applyTranslations } from 'utils/language';
import { SUBCAMPAIGN_STATE_DRAFT } from 'utils/subcampaign';
import { PARTICIPANT_TYPE_SHORTLISTED } from 'utils/campaignParticipants';

import type { Campaign, CheckboxesListItem, List } from 'types';

import './campaign-participants-import-collection-modal.scss';

applyTranslations(require('./campaign-participants-import-collection-modal.i18n.json'));

type OwnProps = {
  campaignId: number,
  channel?: 'tiktok' | 'instagram',
  onCloseModal: () => void
};

type StateProps = {
  campaign: Campaign
};

type DispatchProps = {
  dispatchImportLists: (b: number, a: Array<number>) => Promise<{ data: { existingCreators: Array<string> } }>,
  dispatchReFetch: (b: number, a?: string) => Promise<Array<void>>,
  dispatchError: (a: string) => void
};

type Props = OwnProps & DispatchProps & StateProps;

type State = {
  selectedLists: Array<List>,
  isProcessing: boolean,
  isFinalStep: boolean,
  existingCreators: Array<string>
};

class CampaignParticipantsImportCollectionModal extends React.PureComponent<Props, State> {
  state: State = {
    selectedLists: [],
    isProcessing: false,
    isFinalStep: false,
    existingCreators: []
  };

  handleListItemClick = (item: CheckboxesListItem, list: List): void => {
    if (item.isDisabled || !list) {
      return;
    }

    if (!item.isSelected) {
      this.setState((state) => ({
        selectedLists: state.selectedLists.concat([list])
      }));
    } else {
      this.setState((state) => ({
        selectedLists: state.selectedLists.filter(checkedList => list.id !== checkedList.id)
      }));
    }
  };

  handleCloseModal = async (closeWithoutProcessing: boolean = false) => {
    const { campaignId, onCloseModal, dispatchImportLists, dispatchError } = this.props;
    const { selectedLists } = this.state;

    if (closeWithoutProcessing) {
      onCloseModal();
      return;
    }

    this.setState({
      isProcessing: true
    });

    try {
      const { data } = await dispatchImportLists(campaignId, selectedLists.map(list => list.id));
      const existingCreators = data.existingCreators;
      this.setState({
        isProcessing: false,
        isFinalStep: true,
        existingCreators
      });
    } catch (error) {
      const errorMsg = error?.response?.data?.detail || '';
      const isCreatorNotApproved = errorMsg.includes('not yet approved');

      if (isCreatorNotApproved) {
        dispatchError(errorMsg);
        onCloseModal();
        return;
      }

      dispatchError(__t('campaign_participants_import_collection_modal.import_error'));
      onCloseModal();
    }
  };

  handleCloseWithFinalCheck = () => {
    const { onCloseModal, dispatchReFetch, campaignId, channel } = this.props;
    const { isFinalStep } = this.state;

    if (isFinalStep) {
      dispatchReFetch(campaignId, channel);
    }

    onCloseModal();
  };

  renderFinalStep() {
    const { campaign } = this.props;
    const { selectedLists, existingCreators } = this.state;
    const collections = selectedLists.map(list => list.label).join(', ');

    return (
      <ModalFrame title={__t('campaign_participants_import_collection_modal.modal_title')}>
        <CollectionImportedModalContent
          collectionLabel={collections}
          minHeight={352}
          onCloseModal={this.handleCloseWithFinalCheck}
          selectedCampaigns={[campaign]}
          existingCreators={existingCreators.map(name => `${name} – ${campaign.name}`)}
        />
      </ModalFrame>
    );
  }

  renderGenericList() {
    const { selectedLists } = this.state;

    return (
      <GenericListsModal
        hideCount
        modalHeader={__t('campaign_participants_import_collection_modal.title_label')}
        modalSubTitle={__t('campaign_participants_import_collection_modal.subtitle_label')}
        selectedItems={selectedLists.map(list => list.id)}
        onCloseModal={this.handleCloseModal}
        onListItemClick={this.handleListItemClick}
      />
    );
  }

  render() {
    const { isProcessing, isFinalStep } = this.state;

    if (isProcessing) {
      return (
        <div className="campaign-participants-import-collection-modal__processing">
          <Spinner
            position="inline"
            size="small"
            color="light"
          />
        </div>
      );
    }

    return (
      <React.Fragment>
        {isFinalStep ? this.renderFinalStep() : this.renderGenericList()}
        <div className="campaign-participants-import-collection-modal__close-button">
          <SVGDynamicIcon
            name="cross-custom-fg"
            width={15}
            height={15}
            customColor="grey"
            onClick={this.handleCloseWithFinalCheck}
            data={{ qa: 'close-modal' }}
          />
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = ({ campaignBrief }): StateProps => ({
  campaign: campaignBrief.brief
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchImportLists: (campaignId: number, collectionIds: Array<number>) =>
    dispatch(importCollection(campaignId, collectionIds)),

  dispatchReFetch: (campaignId: number, channel?: string) => Promise.all([
    dispatch(fetchParticipants(campaignId, PARTICIPANT_TYPE_SHORTLISTED)),
    dispatch(fetchCampaignOverview(campaignId)),
    dispatch(fetchCampaignDashboardStatistics('creator', campaignId, SUBCAMPAIGN_STATE_DRAFT, channel))
  ]),

  dispatchError: (message: string) => dispatch(danger(message))
});

export default connect(mapStateToProps, mapDispatchToProps)(CampaignParticipantsImportCollectionModal);
